/**
 * Recording sinks — where the live BWF byte stream lands.
 *
 * Three implementations:
 *   - UserDirSink: user-picked directory (File System Access API,
 *     `showDirectoryPicker`). Writes through a FileSystemWritableFileStream
 *     opened with `keepExistingData: true`; header write-backs use positioned
 *     writes. NOTE: the spec commits writable-stream content to the real file
 *     only on close(), so mid-session crash recovery is NOT guaranteed in this
 *     mode — it is in OPFS sync-access mode.
 *   - OpfsSink: origin-private file system (`navigator.storage.getDirectory`).
 *     Prefers a FileSystemSyncAccessHandle (durable flush(), available in
 *     workers; throws on the main thread) and falls back to createWritable.
 *     Works in headless Chrome — the E2E-test main path.
 *   - MemorySink: in-memory, for unit tests and previews.
 *
 * All writes are positional under the hood so append + header patch can
 * interleave safely.
 */

export interface RecordingSink {
  /** Create/truncate the target file. Must be called before write/patch. */
  open(fileName: string): Promise<void>
  /** Append `bytes` at the current end of file. */
  append(bytes: Uint8Array): Promise<void>
  /** Overwrite `bytes.length` bytes at absolute `offset` (header write-back). */
  patch(offset: number, bytes: Uint8Array): Promise<void>
  /** Hint that now is a good time to make bytes durable (3 s tick). */
  sync(): Promise<void>
  /** Finalize the file. Idempotent. */
  close(): Promise<void>
  /** Full file content. Valid after close(). */
  getBlob(): Promise<Blob>
  /** Write a sibling file (e.g. the .json sidecar) in the same directory. */
  writeSidecar(fileName: string, content: string): Promise<void>
  readonly fileName: string
}

// FileSystemSyncAccessHandle is typed in lib.webworker; when only lib.dom is
// loaded it may be missing, so declare the minimal surface we use.
interface SyncAccessHandleLike {
  write(buffer: Uint8Array, options?: { at?: number }): number
  flush(): void
  close(): void
  getSize(): number
}

/** Base class implementing the writable-stream strategy shared by both FS sinks. */
abstract class FileSystemSinkBase implements RecordingSink {
  protected fileHandle?: FileSystemFileHandle
  protected writable?: FileSystemWritableFileStream
  protected syncHandle?: SyncAccessHandleLike
  protected appendPos = 0
  protected closed = false
  fileName = ''

  protected abstract getDirectory(): Promise<FileSystemDirectoryHandle>

  /** Whether to attempt createSyncAccessHandle (OPFS-only capability). */
  protected get trySyncAccess(): boolean {
    return false
  }

  async open(fileName: string): Promise<void> {
    // Sinks are reused across takes (one per recorder) — reset the close
    // latch or take 2+ would skip sync()/close() and upload a 0-byte file.
    this.closed = false
    this.fileName = fileName
    const dir = await this.getDirectory()
    this.fileHandle = await dir.getFileHandle(fileName, { create: true })

    if (this.trySyncAccess) {
      try {
        const fh = this.fileHandle as FileSystemFileHandle & {
          createSyncAccessHandle?: () => Promise<SyncAccessHandleLike>
        }
        if (typeof fh.createSyncAccessHandle === 'function') {
          this.syncHandle = await fh.createSyncAccessHandle()
          // Truncate any stale content from a previous take with this name.
          ;(this.syncHandle as SyncAccessHandleLike & { truncate?: (n: number) => void }).truncate?.(0)
          this.appendPos = 0
          return
        }
      } catch {
        // Main thread or unsupported — fall through to createWritable.
      }
    }

    this.writable = await this.fileHandle.createWritable({ keepExistingData: false })
    this.appendPos = 0
  }

  async append(bytes: Uint8Array): Promise<void> {
    if (this.syncHandle) {
      this.syncHandle.write(bytes, { at: this.appendPos })
    } else if (this.writable) {
      await this.writable.write({ type: 'write', position: this.appendPos, data: bytes })
    } else {
      throw new Error('RecordingSink.append: sink not open')
    }
    this.appendPos += bytes.length
  }

  async patch(offset: number, bytes: Uint8Array): Promise<void> {
    if (this.syncHandle) {
      this.syncHandle.write(bytes, { at: offset })
    } else if (this.writable) {
      // Positioned write; does NOT move our tracked append position.
      await this.writable.write({ type: 'write', position: offset, data: bytes })
    } else {
      throw new Error('RecordingSink.patch: sink not open')
    }
  }

  async sync(): Promise<void> {
    if (this.syncHandle) {
      // Real durability barrier — cheap.
      this.syncHandle.flush()
      return
    }
    // Writable-stream mode (main thread; sync access handles are worker-only):
    // the spec commits stream content to the real file ONLY on close(), so a
    // crash would leave a 0-byte file. Close + reopen (keepExistingData) at
    // each sync tick → a crash loses at most the last patch interval (~3 s).
    // NOTE: reopen copies the committed bytes into a fresh swap, so cost grows
    // with file size — fine for takes, should move to a worker SyncAccessHandle
    // for long-form sessions (tracked gap).
    if (this.writable && this.fileHandle && !this.closed) {
      await this.writable.close()
      this.writable = await this.fileHandle.createWritable({ keepExistingData: true })
    }
  }

  async close(): Promise<void> {
    if (this.closed) return
    this.closed = true
    if (this.syncHandle) {
      this.syncHandle.flush()
      this.syncHandle.close()
      this.syncHandle = undefined
    }
    if (this.writable) {
      await this.writable.close()
      this.writable = undefined
    }
  }

  async getBlob(): Promise<Blob> {
    if (!this.fileHandle) throw new Error('RecordingSink.getBlob: sink never opened')
    return this.fileHandle.getFile()
  }

  async writeSidecar(fileName: string, content: string): Promise<void> {
    const dir = await this.getDirectory()
    const fh = await dir.getFileHandle(fileName, { create: true })
    const w = await fh.createWritable({ keepExistingData: false })
    await w.write(content)
    await w.close()
  }
}

/** Sink writing into a user-picked directory (File System Access API). */
export class UserDirSink extends FileSystemSinkBase {
  constructor(private readonly dir: FileSystemDirectoryHandle) {
    super()
  }

  protected getDirectory(): Promise<FileSystemDirectoryHandle> {
    return Promise.resolve(this.dir)
  }
}

/** Sink writing into the origin-private file system (OPFS). */
export class OpfsSink extends FileSystemSinkBase {
  private dir?: FileSystemDirectoryHandle

  protected override get trySyncAccess(): boolean {
    return true
  }

  protected async getDirectory(): Promise<FileSystemDirectoryHandle> {
    if (!this.dir) {
      this.dir = await navigator.storage.getDirectory()
    }
    return this.dir
  }
}

/** In-memory sink for unit tests and previews. */
export class MemorySink implements RecordingSink {
  fileName = ''
  bytes = new Uint8Array(0)
  sidecars = new Map<string, string>()
  syncCount = 0
  private closed = false

  async open(fileName: string): Promise<void> {
    this.fileName = fileName
    this.bytes = new Uint8Array(0)
    this.closed = false
  }

  async append(bytes: Uint8Array): Promise<void> {
    const next = new Uint8Array(this.bytes.length + bytes.length)
    next.set(this.bytes, 0)
    next.set(bytes, this.bytes.length)
    this.bytes = next
  }

  async patch(offset: number, bytes: Uint8Array): Promise<void> {
    if (offset + bytes.length > this.bytes.length) {
      throw new Error('MemorySink.patch: write past end of file')
    }
    this.bytes.set(bytes, offset)
  }

  async sync(): Promise<void> {
    this.syncCount += 1
  }

  async close(): Promise<void> {
    this.closed = true
  }

  async getBlob(): Promise<Blob> {
    return new Blob([this.bytes.slice().buffer], { type: 'audio/wav' })
  }

  async writeSidecar(fileName: string, content: string): Promise<void> {
    this.sidecars.set(fileName, content)
  }

  get isClosed(): boolean {
    return this.closed
  }
}
